/**
 * A child's own things, and what they are wearing
 * ([Avatar](../../../docs/AVATAR.md), [Economy](../../../docs/ECONOMY.md#the-wardrobe)).
 *
 * **The markup for every item is local.** It comes from the generated catalogue
 * in `@capytalk/tokens`, which is committed and checked in CI — never from the
 * network. The server sends *ids*, and an id that is not in the catalogue draws
 * nothing. That matters more than usual here: this is the one screen that puts
 * SVG into the DOM, and it must never be possible for a response to decide what
 * that SVG is.
 *
 * The child is drawn as their own animal, wearing what they have on, and can
 * swap animals in place ([D-158](../../../docs/DECISIONS.md#d-158)) — the
 * clothes stay on whoever is underneath.
 */

import { type Slot, SLOTS } from '@capytalk/core';
import { DYES, type Dye, avatarSvg, dyed, wardrobeItem } from '@capytalk/tokens';
import { useEffect, useState } from 'react';
import AnimalPicker from './AnimalPicker.js';
import { type Client, type Trouble, troubleFrom } from './api.js';
import DyeScreen from './Dye.js';
import Sneakers from './Sneakers.js';

const SLOT_NAMES: Readonly<Record<Slot, string>> = {
  hat: 'Hats',
  glasses: 'Glasses',
  top: 'Tops',
  neck: 'Necks',
  shoes: 'Shoes',
  held: 'To hold',
};

/**
 * The catalogue keys items by `slot.id`; the shop and the database key them by
 * `id` alone, because a content id is one slug ([D-086](../../../docs/DECISIONS.md#d-086)).
 * This is the one place the two meet.
 */
const lookUp = (slot: string, itemId: string) => wardrobeItem(`${slot}.${itemId}`);

export function Thing({
  slot,
  itemId,
  dye,
  size = 96,
}: {
  readonly slot: string;
  readonly itemId: string;
  readonly dye: string | null;
  readonly size?: number;
}) {
  const item = lookUp(slot, itemId);
  // An outfit can name something the catalogue no longer has. Drawing nothing
  // beats drawing a broken box.
  if (item === undefined) return null;

  const colour = dye !== null && dye in DYES ? (dye as Dye) : undefined;
  return (
    <svg
      viewBox="0 0 160 240"
      width={size}
      height={(size * 240) / 160}
      aria-hidden="true"
      // Local, generated, CI-checked markup keyed by an id — never anything a
      // response chose. See the note at the top of this file.
      dangerouslySetInnerHTML={{ __html: dyed(item, colour) }}
    />
  );
}

export default function Wardrobe({ client }: { readonly client: Client }) {
  const [owned, setOwned] = useState<
    readonly { id: string; slot: string; name: string }[]
  >([]);
  const [worn, setWorn] = useState<
    Record<string, { itemId: string; dye: string | null } | null>
  >({});
  /** Which animal this child is, and the card behind them. */
  const [look, setLook] = useState<{ species: string; background: string }>({
    species: 'fox',
    background: '#eaf2ff',
  });

  /** The thing whose colours are open, if any ([D-138](../../../docs/DECISIONS.md#d-138)). */
  const [dyeing, setDyeing] = useState<{
    slot: string;
    itemId: string;
    name: string;
  } | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [loaded, setLoaded] = useState(false);
  /** The animal picker is open (D-158). */
  const [picking, setPicking] = useState(false);
  /** In flight. The button goes quiet rather than queueing six shuffles. */
  const [shuffling, setShuffling] = useState(false);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve` because a server older than this app has no
    // `shop` router and the call *throws* rather than rejecting (D-066).
    Promise.resolve()
      .then(() => Promise.all([client.shop.shelf.query(), client.shop.loadout.query()]))
      .then(([shelf, loadout]) => {
        if (!live) return;
        setOwned(shelf.items.filter((one) => one.owned));
        // Read defensively. A server one deploy behind this app answers with the
        // bare loadout this route used to return, and reaching into a `worn`
        // that is not there white-screens the tab for everybody until the
        // deploy catches up ([D-066](../../../docs/DECISIONS.md#d-066)).
        setWorn(loadout.worn ?? {});
        setLook({
          species: loadout.species ?? 'fox',
          background: loadout.background ?? '#eaf2ff',
        });
        setLoaded(true);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client]);

  const put = (slot: string, itemId: string | null) => {
    // Optimistic, because taking a hat on and off should feel like taking a hat
    // on and off. The server is the one that decides, and its answer replaces
    // this the moment it arrives.
    //
    // The previous value is captured *before* the update, not read back inside
    // the failure handler: by then the state already holds the optimistic one,
    // so "putting it back" would put back the thing being undone.
    const before = worn[slot] ?? null;
    // Put on in its own colour. A bought colour is chosen on the colour screen,
    // where it is previewed first (D-138).
    setWorn((was) => ({
      ...was,
      [slot]: itemId === null ? null : { itemId, dye: null },
    }));
    Promise.resolve()
      .then(() => client.shop.wear.mutate({ slot, itemId, dye: null }))
      .then((next) => {
        setWorn(next.worn ?? {});
      })
      .catch(() => {
        // Put it back, rather than leaving a lie on the screen that the next
        // reload would silently correct.
        setWorn((was) => ({ ...was, [slot]: before }));
      });
  };

  /**
   * Let the wardrobe pick ([D-108](../../../docs/DECISIONS.md#d-108)).
   *
   * Not optimistic, unlike `put`: there is nothing to be optimistic *about* —
   * the whole answer is the surprise, and guessing at it locally would mean
   * drawing one outfit and then replacing it with another a moment later.
   */
  const surprise = () => {
    if (shuffling) return;
    setShuffling(true);
    Promise.resolve()
      .then(() => client.shop.shuffle.mutate())
      .then((next) => {
        setWorn(next.worn ?? {});
      })
      // A shuffle that did not happen leaves the outfit exactly as it was,
      // which is already what the screen is showing. Nothing to undo.
      .catch(() => undefined)
      .finally(() => {
        setShuffling(false);
      });
  };

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          Your things would not load. Please try again.
        </p>
      </div>
    );
  }

  if (dyeing !== null) {
    return (
      <DyeScreen
        client={client}
        slot={dyeing.slot}
        itemId={dyeing.itemId}
        name={dyeing.name}
        worn={worn}
        look={look}
        onDone={(next) => {
          if (next !== null) setWorn(next);
          setDyeing(null);
        }}
      />
    );
  }

  if (picking) {
    return (
      <AnimalPicker
        client={client}
        look={{ ...look, worn }}
        onDone={(saved) => {
          if (saved !== null) {
            setLook({ species: saved.species, background: saved.background });
            setWorn(saved.worn);
          }
          setPicking(false);
        }}
      />
    );
  }

  return (
    <div className="grow pad">
      {/*
        The child themselves, wearing what they have on. One `uid` per rendered
        avatar: two on a page sharing gradient ids would paint the second one in
        the first one's colours.
      */}
      <div style={{ display: 'grid', placeItems: 'center' }}>
        <svg
          viewBox="0 0 160 240"
          width={150}
          height={225}
          role="img"
          aria-label={`Your ${look.species}`}
          // Local, generated, CI-checked markup — see the note at the top.
          dangerouslySetInnerHTML={{
            __html: avatarSvg({
              species: look.species,
              background: look.background,
              loadout: worn,
              uid: 'me',
            }),
          }}
        />
      </div>

      {/*
        Directly under the avatar, where 15-wardrobe.html puts it. Hidden until
        there is something to shuffle: a button a child can press and watch do
        nothing is worse than no button.
      */}
      {loaded && (
        <div style={{ display: 'grid', placeItems: 'center', marginTop: 8 }}>
          <div className="row" style={{ gap: 8, justifyContent: 'center' }}>
            {/* Mockup 15's "Change animal": free, any time (D-158). */}
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setPicking(true);
              }}
              style={{ minHeight: 48 }}
            >
              <span aria-hidden="true">🐾</span> Change animal
            </button>
            {owned.length > 0 && (
              <button
                type="button"
                className="btn ghost"
                onClick={surprise}
                disabled={shuffling}
                style={{ minHeight: 48 }}
              >
                🎲 Surprise me
              </button>
            )}
          </div>
          {owned.length > 0 && (
            <p className="tiny" style={{ marginTop: 4 }}>
              The wardrobe picks. You can always change it back.
            </p>
          )}
        </div>
      )}

      <Sneakers
        text={
          owned.length === 0
            ? 'Nothing yet! Play a bit and open a chest.'
            : 'Try things on — you can change your mind.'
        }
        audioRef={null}
        size={72}
      />

      {loaded && owned.length === 0 && (
        <p className="sub" style={{ marginTop: 8 }}>
          Things you win in chests or buy in the shop turn up here.
        </p>
      )}

      {SLOTS.map((slot) => {
        const mine = owned.filter((one) => one.slot === slot);
        if (mine.length === 0) return null;
        const on = worn[slot] ?? null;

        return (
          <section key={slot} style={{ marginTop: 16 }}>
            <h3>{SLOT_NAMES[slot]}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
              {mine.map((item) => {
                const wearing = on?.itemId === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={wearing ? 'btn' : 'btn ghost'}
                    aria-pressed={wearing}
                    aria-label={wearing ? `Take off ${item.name}` : `Wear ${item.name}`}
                    onClick={() => {
                      put(slot, wearing ? null : item.id);
                    }}
                    style={{ display: 'grid', placeItems: 'center', padding: 6 }}
                  >
                    <Thing
                      slot={slot}
                      itemId={item.id}
                      dye={wearing ? (on?.dye ?? null) : null}
                      size={64}
                    />
                    <span className="tiny">{item.name}</span>
                  </button>
                );
              })}
            </div>
            {on !== null && mine.some((item) => item.id === on.itemId) && (
              <button
                type="button"
                className="chip"
                style={{ marginTop: 8, minHeight: 44 }}
                onClick={() => {
                  setDyeing({
                    slot,
                    itemId: on.itemId,
                    name: mine.find((item) => item.id === on.itemId)?.name ?? on.itemId,
                  });
                }}
              >
                <span aria-hidden="true">🎨</span> Colours for the{' '}
                {mine.find((item) => item.id === on.itemId)?.name ?? on.itemId}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
